import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'react-toastify';
import DashboardLayout from '../Layout/DashboardLayout';
import LoadingSpinner from '../Common/LoadingSpinner';
import { getInitials } from '../../utils/helpers';

const UserDetailModal = ({ user, onClose, onAction, onPricingUpdate, onPasswordReset, onDetailsUpdate, onImpersonate, onApprovalUpdate }) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [duration, setDuration] = useState(30);

  const [pricingData, setPricingData] = useState({
    text: 0.10,
    template: 0.20,
    marketing: 1.00,
    utility: 0.20,
    authentication: 0.15,
    image: 0.10,
    document: 0.10,
    video: 0.15,
    audio: 0.10,
    location: 0.10,
    contacts: 0.10,
    interactive: 0.12
  });

  const [editData, setEditData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    status: user?.status || 'active'
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user?.messagePricing) {
      setPricingData({
        ...pricingData,
        ...user.messagePricing
      });
    }
    if (user) {
      setEditData({
        name: user.name,
        email: user.email,
        status: user.status
      });
    }
  }, [user]);

  if (!user) return null;

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTotalBilling = () => {
    let total = 0;
    const msgStats = user.stats?.messages || [];
    msgStats.forEach(stat => {
      const rate = user.messagePricing?.[stat._id] || pricingData[stat._id] || 0;
      total += (stat.total * rate);
    });
    return total;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content admin-user-detail-modal">
        <div className="modal-header">
          <div className="d-flex align-items-center gap-3">
            <div className="user-avatar initials-large">
              {getInitials(user.name)}
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h2 className="mb-0">{user.name}</h2>
                {user.isApproved ? (
                  <span className="badge badge-success-alt"><i className="fas fa-check-shield"></i> Approved</span>
                ) : (
                  <span className="badge badge-warning-alt"><i className="fas fa-clock"></i> Pending Approval</span>
                )}
              </div>
              <p className="text-muted mb-0 small">{user.email}</p>
            </div>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn--sm btn--primary" onClick={() => onImpersonate(user._id)}>
              <i className="fas fa-user-secret"></i> Login as User
            </button>
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>
        </div>

        <div className="modal-tabs">
          {['basic', 'billing', 'approval', 'pricing', 'features', 'waba', 'wallet', 'security'].map(tab => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        <div className="modal-body">
          {activeTab === 'basic' && (
            <div className="detail-section animate-fade-in">
              <div className="row">
                <div className="col-md-6">
                  <h3>Basic Information</h3>
                  <form onSubmit={(e) => { e.preventDefault(); onDetailsUpdate(user._id, editData); }} className="admin-form">
                    <div className="form-group mb-3">
                      <label>Full Name</label>
                      <input type="text" className="form-control" value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} />
                    </div>
                    <div className="form-group mb-3">
                      <label>Email</label>
                      <input type="email" className="form-control" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
                    </div>
                    <div className="form-group mb-3">
                      <label>Status</label>
                      <select className="form-control" value={editData.status} onChange={(e) => setEditData({ ...editData, status: e.target.value })}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="suspended">Suspended</option>
                      </select>
                    </div>
                    <button type="submit" className="btn btn--primary">Update Details</button>
                  </form>
                </div>
                <div className="col-md-6">
                  <h3>Quick Stats</h3>
                  <div className="stats-box p-3 border rounded bg-light">
                    <div className="mb-2"><strong>Wallet:</strong> ₹{user.walletBalance?.toFixed(2)}</div>
                    <div className="mb-2"><strong>WABA:</strong> {user.whatsappNumber || 'None'}</div>
                    <div className="mb-2"><strong>Joined:</strong> {formatDate(user.createdAt)}</div>
                    <div className="mb-2"><strong>Approved:</strong> {user.approvedAt ? formatDate(user.approvedAt) : 'Pending'}</div>
                    <div className="mb-0"><strong>Expires:</strong> <span className={user.subscription?.expiresAt && new Date(user.subscription.expiresAt) < new Date() ? 'text-danger fw-bold' : ''}>{formatDate(user.subscription?.expiresAt)}</span></div>
                  </div>
                  
                  <div className="mt-4">
                    <h3>Flow Executions</h3>
                    <div className="executions-card p-3 border rounded border-primary bg-primary-light">
                      <div className="display-4 fw-bold">{user.stats?.flows?.totalExecutions || 0}</div>
                      <div className="small text-muted">Total system-wide automation triggers</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="detail-section animate-fade-in">
              <div className="billing-header d-flex justify-content-between align-items-center mb-4">
                <h3>Message Category Billing</h3>
                <div className="total-billing h4 mb-0 text-primary">
                  Total System Cost: ₹{calculateTotalBilling().toFixed(2)}
                </div>
              </div>
              
              <div className="table-responsive">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Sent</th>
                      <th>Delivered</th>
                      <th>Read</th>
                      <th>Failed</th>
                      <th>Rate</th>
                      <th>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(user.stats?.messages || []).map(stat => {
                      const rate = user.messagePricing?.[stat._id] || pricingData[stat._id] || 0;
                      return (
                        <tr key={stat._id}>
                          <td className="text-capitalize fw-bold">{stat._id}</td>
                          <td>{stat.total}</td>
                          <td>{stat.delivered}</td>
                          <td>{stat.read}</td>
                          <td>{stat.failed}</td>
                          <td>₹{rate.toFixed(2)}</td>
                          <td className="fw-bold">₹{(stat.total * rate).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                    {(!user.stats?.messages || user.stats.messages.length === 0) && (
                      <tr><td colSpan="7" className="text-center p-4">No message data available</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'approval' && (
            <div className="detail-section animate-fade-in">
              <h3>Account Approval & Subscription</h3>
              <div className="approval-card p-4 border rounded bg-light text-center">
                {user.isApproved ? (
                  <div className="mb-4">
                    <div className="display-6 text-success"><i className="fas fa-check-circle"></i> Account Approved</div>
                    <p className="text-muted">Approved on {formatDate(user.approvedAt)}</p>
                  </div>
                ) : (
                  <div className="mb-4">
                    <div className="display-6 text-warning"><i className="fas fa-clock"></i> Pending Admin Approval</div>
                    <p>This user cannot access the portal until approved.</p>
                  </div>
                )}

                <div className="form-group mb-4" style={{ maxWidth: '300px', margin: '0 auto' }}>
                  <label className="form-label">Subscription Duration (Days)</label>
                  <div className="input-group">
                    <input 
                      type="number" 
                      className="form-control" 
                      value={duration} 
                      onChange={(e) => setDuration(e.target.value)}
                      min="1"
                    />
                    <span className="input-group-text">Days</span>
                  </div>
                  <div className="mt-2 text-muted x-small">Set the expiry date from today (e.g. 30, 365)</div>
                </div>

                <div className="d-flex justify-content-center gap-3">
                  <button 
                    className={`btn btn--lg ${user.isApproved ? 'btn--warning' : 'btn--success'}`}
                    onClick={() => onApprovalUpdate(user._id, !user.isApproved, duration)}
                  >
                    {user.isApproved ? 'Revoke Approval' : 'Approve & Enable Account'}
                  </button>
                  {user.isApproved && (
                    <button 
                      className="btn btn--lg btn--primary-outline"
                      onClick={() => onApprovalUpdate(user._id, true, duration)}
                    >
                      Extend Subscription
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="detail-section animate-fade-in">
              <h3>Custom Messaging Pricing</h3>
              <p className="text-muted small mb-4 italic">Override default platform pricing for this user.</p>
              <div className="pricing-grid-admin">
                {Object.keys(pricingData).map(type => (
                  <div className="pricing-input-group" key={type}>
                    <label>{type.charAt(0).toUpperCase() + type.slice(1)}</label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={pricingData[type]}
                        onChange={(e) => setPricingData({ ...pricingData, [type]: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn btn--primary mt-4" onClick={() => onPricingUpdate(user._id, pricingData)}>
                Save Pricing Changes
              </button>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="detail-section animate-fade-in">
              <h3>Limits & Feature Permissions</h3>
              <div className="feature-controls">
                <div className="form-group mb-4">
                  <label className="form-label">Workflow/Flow Limit</label>
                  <div className="d-flex gap-2" style={{ maxWidth: '200px' }}>
                    <input
                      type="number"
                      className="form-control"
                      defaultValue={user.flowLimit || 5}
                      id={`flow-limit-modal-${user._id}`}
                      min="0"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Enabled Modules</label>
                  <div className="features-checkbox-grid">
                    {['campaigns', 'analytics', 'contacts', 'templates', 'automation', 'reports'].map(feature => (
                      <label key={feature} className="checkbox-label p-2 border rounded">
                        <input
                          type="checkbox"
                          defaultChecked={user.enabledFeatures?.includes(feature)}
                          id={`feature-modal-${feature}-${user._id}`}
                        />
                        {feature.charAt(0).toUpperCase() + feature.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  className="btn btn--primary mt-4"
                  onClick={() => {
                    const limit = document.getElementById(`flow-limit-modal-${user._id}`).value;
                    const selectedFeatures = ['campaigns', 'analytics', 'contacts', 'templates', 'automation', 'reports'].filter(
                      f => document.getElementById(`feature-modal-${f}-${user._id}`).checked
                    );
                    onAction(user._id, 'updateFeatures', { features: selectedFeatures, flowLimit: limit });
                  }}
                >
                  Save Permissions & Limits
                </button>
              </div>
            </div>
          )}

          {activeTab === 'waba' && (
            <div className="detail-section animate-fade-in">
              <h3>WhatsApp Business API Details</h3>
              {user.whatsappAccounts?.length > 0 ? (
                user.whatsappAccounts.map((acc, index) => (
                  <div key={index} className="waba-card p-3 border rounded mb-3 bg-light">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h4 className="mb-0 text-primary">{acc.whatsappNumber}</h4>
                      <span className="badge badge-success">Connected</span>
                    </div>
                    <div className="stats-mini-grid mt-3">
                      <div className="mini-stat bg-white shadow-sm p-2 border rounded">
                        <span className="d-block small text-muted">Phone ID</span>
                        <code className="x-small">{acc.phoneNumberId}</code>
                      </div>
                      <div className="mini-stat bg-white shadow-sm p-2 border rounded">
                        <span className="d-block small text-muted">WABA ID</span>
                        <code className="x-small">{acc.businessAccountId}</code>
                      </div>
                    </div>
                    <div className="mt-3 p-2 bg-white rounded border">
                      <span className="d-block small text-muted mb-1">Access Token</span>
                      <div className="text-truncate x-small text-muted">{acc.accessToken?.substring(0, 20)}...</div>
                    </div>
                    {user.interaktDetails?.isRegistered && (
                      <div className="mt-3 p-2 bg-white rounded border border-success d-flex align-items-center gap-2">
                        <i className="fas fa-check-circle text-success"></i>
                        <span className="small text-success fw-bold">Interakt Registered</span>
                        <span className="ms-auto x-small text-muted">{formatDate(user.interaktDetails.registeredAt)}</span>
                      </div>
                    )}
                    <div className="mt-2 small text-muted text-end">
                      Connected: {formatDate(acc.connectedAt)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center p-5 border rounded bg-light">
                  <i className="fas fa-mobile-alt fa-3x text-muted mb-3"></i>
                  <p>No WhatsApp Business Accounts connected yet.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'wallet' && (
            <div className="detail-section animate-fade-in">
              <h3>Wallet & Recharge History</h3>
              <div className="table-responsive">
                <table className="admin-table mini-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Category</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {user.walletHistory?.length > 0 ? user.walletHistory.map(th => (
                      <tr key={th._id}>
                        <td className="x-small">{formatDate(th.createdAt)}</td>
                        <td>
                          <span className={`text-uppercase small fw-bold ${th.type === 'credit' ? 'text-success' : 'text-danger'}`}>
                            {th.type}
                          </span>
                        </td>
                        <td className="fw-bold">₹{th.amount.toFixed(2)}</td>
                        <td>
                          <span className={`badge badge-sm ${th.status === 'completed' ? 'badge-success' : th.status === 'failed' ? 'badge-danger' : 'badge-warning'}`}>
                            {th.status}
                          </span>
                        </td>
                        <td className="small">{th.category}</td>
                        <td className="small text-muted">{th.description || '-'}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan="6" className="text-center">No transaction history found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="detail-section animate-fade-in">
              <h3>Admin Password Override</h3>
              <p className="text-warning small mb-4">
                <i className="fas fa-exclamation-triangle"></i> You are about to change this user's password. They will be logged out of all active sessions.
              </p>
              <form onSubmit={(e) => {
                e.preventDefault();
                if (passwordData.newPassword !== passwordData.confirmPassword) {
                  return toast.error("Passwords do not match");
                }
                onPasswordReset(user._id, passwordData.newPassword);
                setPasswordData({ newPassword: '', confirmPassword: '' });
              }} className="admin-form" style={{ maxWidth: '400px' }}>
                <div className="form-group mb-3">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Enter min 6 chars..."
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group mb-4">
                  <label className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    className="form-control"
                    placeholder="Repeat password..."
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    required
                  />
                </div>
                <button type="submit" className="btn btn--danger">Reset User Password</button>
              </form>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000;
          backdrop-filter: blur(4px);
        }
        .modal-content {
          background: white; border-radius: 16px; width: 95%; max-width: 900px; max-height: 90vh; display: flex; flex-direction: column;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        .modal-header { padding: 20px 30px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
        .modal-tabs { display: flex; padding: 0 30px; border-bottom: 1px solid #eee; background: #fafafa; gap: 20px; }
        .tab-btn { 
          padding: 15px 5px; border: none; background: none; font-size: 0.9rem; font-weight: 500; color: #666; cursor: pointer;
          position: relative; transition: all 0.2s;
        }
        .tab-btn:after {
          content: ''; position: absolute; bottom: 0; left: 0; right: 0; height: 3px; background: var(--color-primary);
          transform: scaleX(0); transition: transform 0.2s;
        }
        .tab-btn.active { color: var(--color-primary); }
        .tab-btn.active:after { transform: scaleX(1); }
        .modal-body { padding: 30px; overflow-y: auto; flex: 1; }
        .detail-section h3 { margin-bottom: 20px; font-size: 1.25rem; font-weight: 600; }
        .pricing-grid-admin { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; }
        .pricing-input-group label { display: block; font-size: 0.8rem; margin-bottom: 5px; color: #555; }
        .features-checkbox-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; }
        .checkbox-label { display: flex; align-items: center; gap: 10px; cursor: pointer; transition: background 0.2s; }
        .checkbox-label:hover { background: #f0f0f0; }
        .initials-large { width: 48px; height: 48px; border-radius: 50%; background: var(--color-primary); color: white; display: flex; align-items: center; justify-content: center; font-size: 1.2rem; font-weight: bold; }
        .x-small { font-size: 0.7rem; }
        .waba-card { border-left: 4px solid var(--color-primary) !important; }
        .italic { font-style: italic; }
      `}</style>
    </div>
  );
};

const AdminDashboard = () => {
  const {
    admin,
    dashboardStats,
    getDashboardStats,
    getUsers,
    updateUserWallet,
    updateUserFeatures,
    updateUserStatus,
    updateUserPricing,
    getUserDetails,
    resetUserPassword,
    updateUserDetails,
    updateUserApproval,
    impersonateUser,
    hasPermission
  } = useAdmin();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userFilters, setUserFilters] = useState({
    search: '',
    status: '',
    page: 1,
    limit: 10
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);

  useEffect(() => {
    fetchDashboardData();
    fetchUsers();
  }, [userFilters.page, userFilters.status]);

  const fetchDashboardData = async () => {
    try {
      await getDashboardStats();
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      toast.error('Failed to load dashboard statistics');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsers(userFilters);
      if (response && response.users) {
        setUsers(response.users);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprovalUpdate = async (userId, isApproved, durationDays) => {
    try {
      await updateUserApproval(userId, isApproved, durationDays);
      toast.success(isApproved ? 'User approved successfully' : 'User approval revoked');
      // Refresh
      const details = await getUserDetails(userId);
      setSelectedUser(details.user);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update approval status');
    }
  };

  const handleImpersonate = async (userId) => {
    try {
      await impersonateUser(userId);
      toast.success('Opening user session in new tab...');
    } catch (error) {
      toast.error('Impersonation failed');
    }
  };

  const handlePasswordReset = async (userId, newPassword) => {
    try {
      await resetUserPassword(userId, newPassword);
      toast.success('User password reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleDetailsUpdate = async (userId, userData) => {
    try {
      const response = await updateUserDetails(userId, userData);
      if (response.success) {
        toast.success('User details updated successfully');
        // Refresh user details in modal
        const details = await getUserDetails(userId);
        setSelectedUser(details.user);
        fetchUsers();
      }
    } catch (error) {
      toast.error('Failed to update details');
    }
  };

  const handleUserAction = async (userId, action, value, reason = '') => {
    try {
      switch (action) {
        case 'addFunds':
          await updateUserWallet(userId, value, 'add', reason);
          toast.success(`₹${value} added to user wallet`);
          break;
        case 'subtractFunds':
          await updateUserWallet(userId, value, 'subtract', reason);
          toast.success(`₹${value} deducted from user wallet`);
          break;
        case 'updateStatus':
          await updateUserStatus(userId, value, reason);
          toast.success(`User status updated to ${value}`);
          break;
        case 'updateFeatures':
          await updateUserFeatures(userId, value.features, value.flowLimit);
          toast.success('User features and limits updated successfully');
          break;
        default:
          break;
      }

      // Refresh data
      await fetchUsers();
      await fetchDashboardData();
    } catch (error) {
      console.error(`Failed to ${action}:`, error);
      toast.error(`Failed to ${action}`);
    }
  };

  const handlePricingUpdate = async (userId, pricingData) => {
    try {
      await updateUserPricing(userId, pricingData);
      toast.success('User pricing updated successfully');
      // Refresh user details in modal if open
      const details = await getUserDetails(userId);
      setSelectedUser(details.user);
      // Refresh list
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update pricing');
    }
  };

  const handleQuickAction = (action) => {
    switch (action) {
      case 'export':
        toast.info('Exporting user data to CSV...');
        break;
      case 'notify':
        toast.info('Notification system coming soon');
        break;
      case 'report':
        toast.info('Generating platform report...');
        break;
      default:
        break;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge badge-success',
      inactive: 'badge badge-warning',
      suspended: 'badge badge-danger'
    };
    return badges[status] || 'badge badge-secondary';
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && !dashboardStats) {
    return (
      <DashboardLayout isAdmin>
        <div className="dashboard-loading">
          <LoadingSpinner size="large" message="Loading admin dashboard..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout isAdmin>
      <div className="admin-dashboard">
        {/* Header */}
        <div className="dashboard-header">
          <h1>Admin Dashboard</h1>
          <p>Welcome back, {admin?.name}! Manage your MessagePe Platform.</p>
        </div>

        {/* Stats Overview */}
        {dashboardStats && (
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="stat-icon users">
                <i className="fas fa-users"></i>
              </div>
              <div className="stat-content">
                <h3>{dashboardStats.users?.total || 0}</h3>
                <p>Total Users</p>
                <small>{dashboardStats.users?.pending || 0} pending approval</small>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon wallet">
                <i className="fas fa-wallet"></i>
              </div>
              <div className="stat-content">
                <h3>₹{dashboardStats.wallet?.totalBalance?.toFixed(2) || '0.00'}</h3>
                <p>Total Wallet Balance</p>
                <small>Platform avg: ₹{dashboardStats.wallet?.averageBalance?.toFixed(2) || '0.00'}</small>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon registrations">
                <i className="fas fa-chart-line"></i>
              </div>
              <div className="stat-content">
                <h3>{dashboardStats.platform?.totalMessages || 0}</h3>
                <p>Messages Processed</p>
                <small>{dashboardStats.platform?.totalExecutions || 0} Flow Executions</small>
              </div>
            </div>

            <div className="admin-stat-card">
              <div className="stat-icon messages">
                <i className="fas fa-user-plus"></i>
              </div>
              <div className="stat-content">
                <h3>{dashboardStats.registrations?.thisMonth || 0}</h3>
                <p>New Registrations</p>
                <small>This Month</small>
              </div>
            </div>
          </div>
        )}

        {/* User Management Section */}
        <div className="admin-section">
          <div className="section-header">
            <h2><i className="fas fa-users-cog"></i> User Management</h2>
            <div className="section-actions">
              <form onSubmit={(e) => { e.preventDefault(); fetchUsers(); }} className="d-flex gap-2">
                <input
                  type="text"
                  placeholder="Search users..."
                  className="form-control search-input"
                  value={userFilters.search}
                  onChange={(e) => setUserFilters(prev => ({ ...prev, search: e.target.value }))}
                />
                <select
                  className="form-control"
                  value={userFilters.status}
                  onChange={(e) => setUserFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-search"></i> Search
                </button>
              </form>
            </div>
          </div>

          {/* Users Table */}
          <div className="users-table">
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Wallet</th>
                  <th>Registered</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.length > 0 ? users.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="user-info">
                        {user.profilePicture ? (
                          <img
                            src={user.profilePicture}
                            alt={user.name}
                            className="user-avatar"
                          />
                        ) : (
                          <div className="user-avatar initials">
                            {getInitials(user.name)}
                          </div>
                        )}
                        <div>
                          <div className="user-name">{user.name}</div>
                          <div className="user-phone"><small>{user.whatsappNumber || 'No phone'}</small></div>
                        </div>
                      </div>
                    </td>
                    <td>{user.email}</td>
                    <td>
                      <span className={getStatusBadge(user.status)}>
                        {user.status}
                      </span>
                      {!user.isApproved && (
                        <div className="mt-1"><span className="badge badge-warning-alt x-small">Pending Approval</span></div>
                      )}
                      {user.subscription?.expiresAt && new Date(user.subscription.expiresAt) < new Date() && (
                        <div className="mt-1"><span className="badge badge-danger x-small">Expired</span></div>
                      )}
                    </td>
                    <td>
                      <span className="wallet-balance">
                        ₹{user.walletBalance?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-primary"
                          title="View Details"
                          onClick={async () => {
                            try {
                              const details = await getUserDetails(user._id);
                              setSelectedUser(details.user);
                              setShowUserModal(true);
                            } catch (err) {
                              toast.error('Failed to load user details');
                            }
                          }}
                        >
                          <i className="fas fa-eye"></i>
                        </button>

                        {hasPermission('wallet_management') && (
                          <button
                            className="btn btn-sm btn-success"
                            title="Add Funds"
                            onClick={() => {
                              const amount = prompt('Enter amount to add (₹):');
                              if (amount && !isNaN(parseFloat(amount))) {
                                handleUserAction(user._id, 'addFunds', parseFloat(amount), 'Admin wallet top-up');
                              }
                            }}
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                        )}

                        {hasPermission('user_management') && user.status !== 'suspended' && (
                          <button
                            className="btn btn-sm btn-danger"
                            title="Suspend User"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to suspend this user?')) {
                                handleUserAction(user._id, 'updateStatus', 'suspended', 'Admin action');
                              }
                            }}
                          >
                            <i className="fas fa-ban"></i>
                          </button>
                        )}

                        {hasPermission('user_management') && user.status === 'suspended' && (
                          <button
                            className="btn btn-sm btn-warning"
                            title="Activate User"
                            onClick={() => {
                              handleUserAction(user._id, 'updateStatus', 'active', 'Admin reactivation');
                            }}
                          >
                            <i className="fas fa-check"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">No users found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="admin-section">
          <div className="section-header">
            <h2><i className="fas fa-bolt"></i> Quick Actions</h2>
          </div>

          <div className="quick-actions-grid">
            <div className="action-card">
              <div className="action-icon">
                <i className="fas fa-download"></i>
              </div>
              <h3>Export Users</h3>
              <p>Download user data as CSV</p>
              <button className="btn btn-primary" onClick={() => handleQuickAction('export')}>Export</button>
            </div>

            <div className="action-card">
              <div className="action-icon">
                <i className="fas fa-envelope"></i>
              </div>
              <h3>Send Notification</h3>
              <p>Send email to all users</p>
              <button className="btn btn-primary" onClick={() => handleQuickAction('notify')}>Send</button>
            </div>

            <div className="action-card">
              <div className="action-icon">
                <i className="fas fa-chart-bar"></i>
              </div>
              <h3>Generate Report</h3>
              <p>Platform analytics report</p>
              <button className="btn btn-primary" onClick={() => handleQuickAction('report')}>Generate</button>
            </div>

            <div className="action-card">
              <div className="action-icon">
                <i className="fas fa-cog"></i>
              </div>
              <h3>System Settings</h3>
              <p>Configure platform settings</p>
              <button className="btn btn-primary" onClick={() => handleQuickAction('configure')}>Configure</button>
            </div>
          </div>
        </div>
      </div>
      {showUserModal && (
        <UserDetailModal
          user={selectedUser}
          onClose={() => setShowUserModal(false)}
          onAction={handleUserAction}
          onPricingUpdate={handlePricingUpdate}
          onPasswordReset={handlePasswordReset}
          onDetailsUpdate={handleDetailsUpdate}
          onImpersonate={handleImpersonate}
          onApprovalUpdate={handleApprovalUpdate}
        />
      )}
    </DashboardLayout>
  );
};

export default AdminDashboard;